/**
 * @license
 * Copyright TWISS.IO
 * All Rights Reserved.
 * Licensed under the MIT License.
 */

import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  ViewChild,
  ViewChildren, QueryList,
  ChangeDetectorRef
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { MAT_BOTTOM_SHEET_DATA, MatStepper, MatAutocompleteTrigger } from "@angular/material";
import {
  FormBuilder,
  Validators,
  AbstractControl,
  FormGroup,
  FormArray
} from "@angular/forms";
import { Observable, Subscription, Subject } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { MatBottomSheetRef } from "@angular/material/bottom-sheet";
import {
  MomentDateAdapter,
  MAT_MOMENT_DATE_ADAPTER_OPTIONS
} from "@angular/material-moment-adapter";
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE
} from "@angular/material/core";

import { Submission } from "@app/models";
import { ApiService, AuthService } from "@app/@core/services";
import { PageService } from "@auth/services";
import { AssociatesCaseSubmissionsService } from "@app/@shared/services/associates/case-submissions/case-submissions.service";
import { MessageService } from '@app/@shared/components/message/message.service';

// Depending on whether rollup is used, moment needs to be imported differently.
// Since Moment.js doesn't have a default export, we normally need to import using the `* as`
// syntax. However, rollup creates a synthetic default module and we thus need to import it using
// the `default as` syntax.
import * as _moment from "moment";
// tslint:disable-next-line:no-duplicate-imports
import { default as _rollupMoment } from "moment";

const moment = _rollupMoment || _moment;

// See the Moment.js docs for the meaning of these formats:
// https://momentjs.com/docs/#/displaying/format/
export const update_individual_date_formats = {
  parse: {
    dateInput: "LL"
  },
  display: {
    dateInput: "LL",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "LL",
    monthYearA11yLabel: "MMMM YYYY"
  }
};

import * as Fuse from "fuse.js";

@Component({
  selector: "associates-individual-update-dialog",
  templateUrl: "./individual-update-dialog.component.html",
  styleUrls: ["./individual-update-dialog.component.scss"],
  providers: [
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: update_individual_date_formats }
  ]
})
export class AssociatesIndividualUpdateDialogComponent implements OnInit, OnDestroy {
  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
    private formBuilder: FormBuilder,
    public mbsRef: MatBottomSheetRef,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    public authService: AuthService,
    private pageService: PageService,
    private _notification: MessageService,
    private apiService: ApiService,
    private assSubmissionService: AssociatesCaseSubmissionsService
  ) {
  }

  private subs_getSelections: Subscription;

  is_update: boolean = false;
  processing = new Subject<boolean>();
  selectionsLoaded = new Subject<boolean>();
  authForm_default_values;

  salutations;
  genders;
  races;
  countries;
  residency_status;
  marital_status;
  employment_status;
  educational_levels;
  filteredRaces: Observable<string[]>;

  public authForm: FormGroup = this.formBuilder.group({
    // Individual Record
    relationship_type_slug: [null],
    salutation_slug: [null],
    full_name: [null],
    alias: [null],
    chinese_name: [null],
    nric_no: [null],
    fin_no: [null],
    passport_no: [null],
    gender_slug: [null],
    date_birth: [moment()],
    race_slug: [null],
    country_birth_slug: [null],
    nationality_slug: [null],
    residency_status_slug: [null],
    marital_status_slug: [null],
    smoker: [false],
    selected: [false],
    pdpa: [false],
    education_level_slug: [null],
    education_institution: [null],
    field_of_study: [null],
    employment_status_slug: [null],
    income_range: [null],
    job_title: [null],
    company_name: [null],
    business_nature: [null],

    // Individual :: Contact
    email: [null],
    home_no: [null],
    mobile_no: [null],
    fax_no: [null],

    // Individual :: Address
    block: [null],
    street: [null],
    unit: [null],
    building: [null],
    city: [null],
    postal: [null],
    country_slug: [null]
  });
  get salutation_slug() {
    return this.authForm.controls.salutation_slug;
  }
  get full_name() {
    return this.authForm.controls.full_name;
  }
  get alias() {
    return this.authForm.controls.alias;
  }
  get chinese_name() {
    return this.authForm.controls.chinese_name;
  }
  get nric_no() {
    return this.authForm.controls.nric_no;
  }
  get fin_no() {
    return this.authForm.controls.fin_no;
  }
  get passport_no() {
    return this.authForm.controls.passport_no;
  }
  get gender_slug() {
    return this.authForm.controls.gender_slug;
  }
  get date_birth() {
    return this.authForm.controls.date_birth;
  }
  get race_slug() {
    return this.authForm.controls.race_slug;
  }
  get country_birth_slug() {
    return this.authForm.controls.country_birth_slug;
  }
  get nationality_slug() {
    return this.authForm.controls.nationality_slug;
  }
  get residency_status_slug() {
    return this.authForm.controls.residency_status_slug;
  }
  get marital_status_slug() {
    return this.authForm.controls.marital_status_slug;
  }
  get smoker() {
    return this.authForm.controls.smoker;
  }
  get selected() {
    return this.authForm.controls.selected;
  }
  get pdpa() {
    return this.authForm.controls.pdpa;
  }
  get education_level_slug() {
    return this.authForm.controls.education_level_slug;
  }
  get education_institution() {
    return this.authForm.controls.education_institution;
  }
  get field_of_study() {
    return this.authForm.controls.field_of_study;
  }
  get employment_status_slug() {
    return this.authForm.controls.employment_status_slug;
  }
  get income_range() {
    return this.authForm.controls.income_range;
  }
  get job_title() {
    return this.authForm.controls.job_title;
  }
  get company_name() {
    return this.authForm.controls.company_name;
  }
  get business_nature() {
    return this.authForm.controls.business_nature;
  }
  get email() {
    return this.authForm.controls.email;
  }
  get home_no() {
    return this.authForm.controls.home_no;
  }
  get mobile_no() {
    return this.authForm.controls.mobile_no;
  }
  get fax_no() {
    return this.authForm.controls.fax_no;
  }
  get block() {
    return this.authForm.controls.block;
  }
  get street() {
    return this.authForm.controls.street;
  }
  get unit() {
    return this.authForm.controls.unit;
  }
  get building() {
    return this.authForm.controls.building;
  }
  get city() {
    return this.authForm.controls.city;
  }
  get postal() {
    return this.authForm.controls.postal;
  }
  get country_slug() {
    return this.authForm.controls.country_slug;
  }

  ngOnInit() {
    this.getSelections();
  }

  ngOnDestroy() {
    if (this.subs_getSelections) this.subs_getSelections.unsubscribe();
  }

  getUpdatedValues() {
    const updatedFormValues = {};
    this.authForm['_forEachChild']((control, name) => {
      if (control.value != this.authForm_default_values[name]) {
          updatedFormValues[name] = control.value;
      }
    });
    return updatedFormValues;
  }

  submitForm() {
    this.processing.next(true);
    // Create Submission Record
    let url = "associates/individuals/" + this.data.uuid;
    this.apiService.post(url, this.getUpdatedValues())
      .subscribe(res => {
        if (res.error === false) {
          // Case Created
          this.mbsRef.dismiss();
          this.pageService.triggerParentRefresh(true);
          this._notification.create('success', 'Success! Personal data updated successfully.', { Position: 'top', Style: 'bar', Duration: 2000 });
        }
        this.processing.next(false);
      });
  }

  getSelections() {
    this.subs_getSelections = this.apiService
      .post("selections", {
        lists: [
          "salutation",
          "gender",
          "race",
          "country",
          "residency-status",
          "marital-status",
          "employment-status",
          "educational-level",
          "bank"
        ]
      })
      .subscribe(res => {
        let data = res["data"];
        // console.log(data);
        this.selectionsLoaded.next(true);

        this.salutations = data["salutation"];
        this.genders = data["gender"];
        this.races = data["race"];
        this.countries = data["country"];
        this.residency_status = data["residency-status"];
        this.marital_status = data["marital-status"];
        this.employment_status = data["employment-status"];
        this.educational_levels = data["educational-level"];
        this.filteredRaces = this.race_slug.valueChanges.pipe(
          startWith(""),
          map(value => this._filter(value, this.races))
        );

        this.updateModel(this.data);
      });
  }


  updateModel(data = null) {
    if (data) {
      // Reset Insurance Details
      this.authForm.reset();
      this.authForm.patchValue({
        salutation_slug: data.salutation_slug,
        full_name: data.full_name,
        alias: data.alias,
        chinese_name: data.chinese_name,
        nric_no: data.nric_no,
        fin_no: data.fin_no,
        passport_no: data.passport_no,
        gender_slug: data.gender_slug,
        date_birth: data.date_birth,
        race_slug: data.race_slug,
        country_birth_slug: data.country_birth_slug,
        nationality_slug: data.nationality_slug,
        residency_status_slug: data.residency_status_slug,
        marital_status_slug: data.marital_status_slug,
        smoker: data.smoker,
        selected: data.selected,
        pdpa: data.pdpa,
        education_level_slug: data.education_level_slug,
        education_institution: data.education_institution,
        field_of_study: data.field_of_study,
        employment_status_slug: data.employment_status_slug,
        income_range: data.income_range,
        job_title: data.job_title,
        company_name: data.company_name,
        business_nature: data.business_nature,

        // Individual :: Contact
        email: data.contact_information.email,
        home_no: data.contact_information.home_no,
        mobile_no: data.contact_information.mobile_no,
        fax_no: data.contact_information.fax_no,

        // Individual :: Address
        block: data.address_information.block,
        street: data.address_information.street,
        unit: data.address_information.unit,
        building: data.address_information.building,
        city: data.address_information.city,
        postal: data.address_information.postal,
        country_slug: data.address_information.country_slug
      });

      this.authForm_default_values = this.authForm.value;
    } else {
      this.authForm.patchValue({
        salutation_slug: null,
        full_name: null,
        alias: null,
        chinese_name: null,
        nric_no: null,
        fin_no: null,
        passport_no: null,
        gender_slug: null,
        date_birth: new Date(),
        race_slug: null,
        country_birth_slug: null,
        nationality_slug: null,
        residency_status_slug: null,
        marital_status_slug: null,
        smoker: false,
        selected: false,
        pdpa: false,
        education_level_slug: null,
        education_institution: null,
        field_of_study: null,
        employment_status_slug: null,
        income_range: null,
        job_title: null,
        company_name: null,
        business_nature: null,

        // Individual :: Contact
        email: null,
        home_no: null,
        mobile_no: null,
        fax_no: null,

        // Individual :: Address
        block: null,
        street: null,
        unit: null,
        building: null,
        city: null,
        postal: null,
        country_slug: null
      });
    }
  }

  resetForm(ref = null, data = null) {
    if (!ref) {
      this.authForm.reset();
    } else {
      const form_field = this.authForm.get(ref);
      if (!form_field.validator) return form_field.reset();
      const validator = form_field.validator({} as AbstractControl);
      if (validator && validator.required) form_field.setValue("");
      else form_field.reset();
    }
  }

  private _filter(value, model): string[] {
    const filterValue = value ? value.toLowerCase() : "";
    return model.filter(option =>
      option.slug.toLowerCase().includes(filterValue)
    );
  }
}
